import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
} from "@mui/material";
import moment from "moment";
import { getCustomerBillingHistory } from "../../apis/apiList/billingHistoryApi";

export default function ManageSubscription({
  subscription,       // current subscription object
  products,           // array of Stripe products with default_price
  handleCancel,
  handleResume,
  handleChangePlan,
  billingHistory,
  orgId,   // array of past invoices
  userDetails
}) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [actionType, setActionType] = useState(null); // "switch" | "cancel" | "resume"

  // 🔹 Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  // const [billingHistory, setBillingHistory] = useState([]);

  // useEffect(() => {
  //   async function fetchBillingHistory() {
  //     const res = await getCustomerBillingHistory(orgId, {
  //       headers: {
  //         "Content-Type": "application/json",
  //         "x-access-token": userDetails.jwtToken,
  //       },
  //     });
  //     console.log("billing history without dependency", res);
  //     setBillingHistory(res.billingHistory)
  //   }

  //   fetchBillingHistory();
  // }, []);

  const handleOpenModal = (type, product = null) => {
    console.log("Selected product:", product);
    setActionType(type);
    setSelectedPlan(product);
    setOpenModal(true);
  };
  
  const handleConfirmAction = () => {
    if (actionType === "switch" && selectedPlan) {
      handleChangePlan(selectedPlan.id);
    }
    if (actionType === "cancel") {
      handleCancel();
    }
    if (actionType === "resume") {
      handleResume();
    }
    setOpenModal(false);
    setSelectedPlan(null);
    setActionType(null);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPlan(null);
    setActionType(null);
  };

  const checkProductName = (productId) => {
    const product = products.find((p) => p.id === productId);
    return product ? product.name : "Unknown";
  };

  // Handlers for pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // reset to first page
  };

  // 🔹 slice current page data
  const paginatedHistory =
    billingHistory?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) || [];

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Current Plan
      </Typography>

      {/* Plans */}
      <Grid container spacing={2}>
        {products.map((product) => {
          const isCurrent =
            subscription?.planId === product.id;
        //   const isCurrent =
        //     subscription?.planId === product.default_price?.id;

          return (
            <Grid item xs={12} md={4} key={product.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: isCurrent
                    ? "2px solid #1976d2"
                    : "1px solid #ddd",
                  background: isCurrent ? "aliceblue" : "#f9f9f9",
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography variant="h6">{product.name}</Typography>
                  <Typography variant="h4">
                    ${(product.unit_amount / 100).toFixed(2)} /{" "}
                    {product.interval}
                    {/* ${(product.default_price?.unit_amount / 100).toFixed(2)} /{" "}
                    {product.default_price?.recurring?.interval} */}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    { isCurrent ? '': product.description}
                  </Typography>
                  {/* If this is the active plan */}
                  {isCurrent ? (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontWeight: 'bold' }}
                      >
                        Status:{" "}
                        {subscription.cancel_at_period_end
                          ? `Cancelled (ends at period end) ${moment(
                              subscription.current_period_end
                            ).format("MM/DD/YYYY")}`
                          : `${subscription.status} (renews on ${moment(
                              subscription.current_period_end
                            ).format("MM/DD/YYYY")})`}
                      </Typography>
                      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleOpenModal("cancel", product)}
                          disabled={subscription.cancel_at_period_end}
                        >
                          Cancel Subscription
                        </Button>
                        {subscription.cancel_at_period_end && (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleOpenModal("resume", product)}
                          >
                            Resume
                          </Button>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={() => handleOpenModal("switch", product)}
                    >
                      Switch to this Plan
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Billing History */}
      <Box sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h2">Billing History</Typography>
        <Box
          sx={{
            mt: 2,
            border: "1px solid #ddd",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#f5f5f5" }}>
              <tr>
                <th style={{ padding: "10px", textAlign: "left" }}>DATE</th>
                <th style={{ padding: "10px", textAlign: "left" }}>TYPE</th>
                <th style={{ padding: "10px", textAlign: "left" }}>DESCRIPTION</th>
                <th style={{ padding: "10px", textAlign: "left" }}>STATUS</th>
                <th style={{ padding: "10px", textAlign: "left" }}>RENEW/EXPIRES</th>
                <th style={{ padding: "10px", textAlign: "left" }}>AMOUNT</th>
                <th style={{ padding: "10px", textAlign: "left" }}>INVOICE</th>
              </tr>
            </thead>
            <tbody>
              {paginatedHistory?.length > 0 ? (
                paginatedHistory.map((invoice) => (
                  <tr key={invoice.id}>
                    <td style={{ padding: "10px" }}>
                      {new Date(invoice.periodStart).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "10px" }}>
                      {checkProductName(invoice.plan)}
                    </td>
                    <td style={{ padding: "10px" }}>
                      {invoice.description}
                    </td>
                    <td style={{ padding: "10px" }}>
                      {invoice.status}
                    </td>
                    <td style={{ padding: "10px" }}>
                      {new Date(invoice.periodEnd).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "10px" }}>
                      ${(invoice.amount ).toFixed(2)}
                    </td>
                    <td style={{ padding: "10px" }}>
                      <a
                        href={invoice.invoiceUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#1976d2", textDecoration: "none" }}
                      >
                        Download
                      </a>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ padding: "10px", textAlign: "center" }}>
                    No invoices found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {/* 🔹 Pagination Controls */}
          <TablePagination
            component="div"
            count={billingHistory?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle sx={{fontSize: '18px', fontWeight: 'bold'}}>
          {actionType === "switch" && "Confirm Plan Switch"}
          {actionType === "cancel" && "Cancel Subscription"}
          {actionType === "resume" && "Resume Subscription"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionType === "switch" &&
              `Are you sure you want to switch to the "${selectedPlan?.name}" plan?`}
            {actionType === "cancel" && (
              <>
                Are you sure you want to cancel your subscription?
                <br />
                You’ll still have access until{" "}
                <strong>
                  {subscription?.current_period_end
                    ? new Date(subscription.current_period_end).toLocaleDateString()
                    : "the end of this billing cycle" + subscription.current_period_end}
                </strong>.
              </>
            )}
            {actionType === "resume" &&
              "Do you want to resume your subscription and continue access without interruption?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} variant="outlined">No</Button>
          <Button variant="contained" onClick={handleConfirmAction}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
